// Wave 3B — Commit 3c + Wave 3B Followup (guest parity)
// Reads the current identity's (user OR guest) offline-shopping location +
// enabled preference. Toggle ON requires BOTH offline_shopping_enabled = true
// AND freshness (< 24h).

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface OfflineShoppingLocation {
  lat: number | null;
  lng: number | null;
  updatedAt: string | null;
  enabled: boolean;
  isFresh: boolean;
  loading: boolean;
}

const FRESHNESS_MS = 24 * 60 * 60 * 1000;

function computeIsFresh(updatedAt: string | null): boolean {
  if (!updatedAt) return false;
  const t = new Date(updatedAt).getTime();
  if (!Number.isFinite(t)) return false;
  return Date.now() - t < FRESHNESS_MS;
}

export interface OfflineIdentityInput {
  userId?: string | null;
  guestSessionId?: string | null;
}

function identityKey(input: OfflineIdentityInput | string | null | undefined): string {
  if (typeof input === "string") return input;
  if (input?.userId) return input.userId;
  if (input?.guestSessionId) return `guest:${input.guestSessionId}`;
  return "anon";
}

export function offlineShoppingLocationKey(
  input: OfflineIdentityInput | string | null | undefined,
) {
  return ["offline-shopping-location", identityKey(input)] as const;
}

export function useOfflineShoppingLocation(
  input: OfflineIdentityInput | string | null | undefined,
): OfflineShoppingLocation {
  const identity: OfflineIdentityInput =
    typeof input === "string" || input == null
      ? { userId: input ?? null }
      : input;
  const userId = identity.userId ?? null;
  const guestSessionId = identity.guestSessionId ?? null;

  const q = useQuery({
    queryKey: offlineShoppingLocationKey(identity),
    enabled: !!(userId || guestSessionId),
    staleTime: 60 * 1000,
    queryFn: async () => {
      if (userId) {
        const { data, error } = await supabase
          .from("profiles")
          .select(
            "offline_shopping_lat, offline_shopping_lng, offline_shopping_location_updated_at, offline_shopping_enabled",
          )
          .eq("id", userId)
          .maybeSingle();
        if (error) throw error;
        return {
          lat: (data?.offline_shopping_lat as number | null) ?? null,
          lng: (data?.offline_shopping_lng as number | null) ?? null,
          updatedAt:
            (data?.offline_shopping_location_updated_at as string | null) ?? null,
          enabled: Boolean(
            (data as { offline_shopping_enabled?: boolean } | null)
              ?.offline_shopping_enabled,
          ),
        };
      }
      // Guest branch — SECURITY DEFINER RPC (anon-executable).
      const { data, error } = await (supabase as unknown as {
        rpc: (
          fn: string,
          args: Record<string, unknown>,
        ) => Promise<{ data: unknown; error: unknown }>;
      }).rpc("get_guest_offline_location", {
        p_guest_session_id: guestSessionId,
      });
      if (error) throw error;
      const row = Array.isArray(data) ? (data[0] ?? null) : data;
      const r = row as
        | {
            lat: number | null;
            lng: number | null;
            updated_at: string | null;
            enabled: boolean | null;
          }
        | null;
      return {
        lat: r?.lat ?? null,
        lng: r?.lng ?? null,
        updatedAt: r?.updated_at ?? null,
        enabled: Boolean(r?.enabled),
      };
    },
  });

  const updatedAt = q.data?.updatedAt ?? null;
  return {
    lat: q.data?.lat ?? null,
    lng: q.data?.lng ?? null,
    updatedAt,
    enabled: q.data?.enabled ?? false,
    isFresh: computeIsFresh(updatedAt),
    loading: q.isLoading,
  };
}

/**
 * Back-compat shim — prefer queryClient.invalidateQueries with
 * offlineShoppingLocationKey(identity).
 */
export function invalidateOfflineShoppingLocation(userId?: string | null): void {
  void userId;
}

export { useQueryClient };
